import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parse, isValid } from "date-fns";
import { forwardRef, useState } from "react";
import "./datepicker-icon-right.css";

type CustomDatePickerProps = {
    id?: any,
    name: any,
    className: any,
    value: Date | null;
    onChange: (date: Date | null) => void;
    placeholder?: string;
    dateFormat: string;
    mode?: "full" | "month-year" | "full-time"; // full = normal date, month-year = month/year picker
    minDate?: Date;
    maxDate?: Date;
    includeDates?: Date[];
    disabled?: boolean;
    shouldCloseOnSelect?: boolean;
};
// Typed custom input for react-datepicker
type CustomInputProps = {
    id?: any,
    name: any,
    className: any,
    value?: string;
    onClick?: () => void;
    onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    mode: string;
    dateFormat: string;
    disabled?: boolean;
};
const formatDateInput = (input: string, mode: string, formatStr: string) => {
    let value = input.replace(/\D/g, ""); // keep only numbers

    // Auto-insert slashes and space + colon
    if (mode === "full" && value.length >= 5) {
        value = value.replace(/^(\d{2})(\d{2})(\d{0,4})$/, "$1/$2/$3");
    } else if (mode === "month-year" && value.length >= 3) {
        value = value.replace(/^(\d{2})(\d{0,4})$/, "$1/$2");
    } else if (mode === "full-time") {
        if (value.length >= 5 && value.length <= 8) {
            value = value.replace(/^(\d{2})(\d{2})(\d{0,4})$/, "$1/$2/$3");
        }
        if (value.length >= 9) {
            value = value.replace(
                /^(\d{2})(\d{2})(\d{4})(\d{2})(\d{0,2})$/,
                "$1/$2/$3 $4:$5"
            );
        }
    }
    // Validate when length matches expected
    let parsedDate;
    if (mode === "full" && value.length === 10) {
        parsedDate = parse(value, formatStr, new Date());
    } else if (mode === "month-year" && value.length === 7) {
        parsedDate = parse(value, formatStr, new Date());
    } else if (mode === "full-time" && value.length >= 16) {
        parsedDate = parse(value, formatStr, new Date());
    }
    return value;
};
const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
    ({ id, name, className, value, onClick, onChange, placeholder, mode, dateFormat, disabled }, ref) => {

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const formatted = formatDateInput(e.target.value, mode, dateFormat);
            // If mode is full-time, append AM/PM when length matches
            let finalValue = formatted;
            if (mode === "full-time" && formatted.length === 16 && !/[AP]M$/i.test(formatted)) {
                finalValue += " AM"; // default AM, user can change
            }
            e.target.value = finalValue;
            if (onChange) {
                onChange(e);
            }
        };
        const maxLength =
            mode === "full" ? 10 :
                mode === "month-year" ? 7 :
                    mode === "full-time" ? 19 : 10;

        return (
            <input
                ref={ref}
                type="text"
                id={id ? id : name}
                name={name}
                className={className}
                value={value}
                onClick={onClick}
                onChange={handleInputChange}
                placeholder={placeholder}
                // style={{ padding: "10px" }}
                maxLength={maxLength}
                disabled={disabled}
            />
        )
    }
);
const DatePickerField: React.FC<CustomDatePickerProps> = ({
    id,
    name,
    className,
    value,
    onChange,
    placeholder = "Select date",
    dateFormat,
    mode = "full",
    minDate,
    maxDate,
    includeDates,
    disabled,
    shouldCloseOnSelect
}) => {

    return (
        <DatePicker
            id={id ? id : name}
            name={name}
            className={className}
            selected={value}
            onChange={onChange}
            isClearable={true}
            placeholderText={placeholder}
            dateFormat={dateFormat || (mode === "month-year" ? "MM/yyyy" : "dd/MM/yyyy")}
            showMonthYearPicker={mode === "month-year"}
            showTimeSelect={mode === "full-time"}
            timeIntervals={15}
            showYearDropdown={mode !== "month-year"}
            showMonthDropdown
            minDate={includeDates?.length ? undefined : minDate}
            maxDate={includeDates?.length ? undefined : maxDate}
            includeDates={includeDates?.length ? includeDates : undefined}
            customInput={
                <CustomInput name={name} className={className} placeholder={placeholder}
                    mode={mode} dateFormat={dateFormat} />
            }
            disabled={disabled}
            shouldCloseOnSelect={shouldCloseOnSelect}
            showIcon={true}
            calendarIconClassName="my-calendar-icon"
        />
    )
};
export default DatePickerField;